/** Jobs triage list fetching, with the two things a raw fetch-in-useEffect lacks
 *  (and that we'd otherwise pull in React Query for): in-flight request
 *  de-duplication and a stale-while-revalidate cache. Both live at module scope
 *  so they survive JobsList unmount/remount — which is what makes job → back →
 *  list instant instead of a cold ~2.5s refetch.
 *
 *  Dedupe: concurrent calls with the same filter key share one Promise/network
 *  request. This is what neutralises React 18 StrictMode's dev double-invoke of
 *  the fetch effect (two effect runs → one request), and guards against any
 *  accidental double-call in prod too.
 *
 *  Cache (SWR): a returning visit paints the last result immediately (no
 *  spinner) and then revalidates in the background, swapping fresh data in and
 *  exposing `refreshing` so the UI can show it's updating. We do NOT keep data
 *  "fresh" for a long window — leaving the list and coming back should always
 *  reflect current data. The only skip is a tiny DEDUP_WINDOW that collapses a
 *  burst of mounts (StrictMode's dev double-invoke on a fast response, an
 *  accidental rapid bounce) into one request. Concurrent requests are also
 *  collapsed by the in-flight map below — that, not the window, is what makes
 *  the StrictMode double-fire a single request. */

import { useEffect, useState } from "react";
import { jobsApi, ApiError } from "@/api";
import type { JobListFilters, JobsListResponse } from "@/types";

/** Skip a fresh revalidation only if we fetched within this window — just long
 *  enough to absorb a synchronous burst of mounts (StrictMode, a double bounce).
 *  Anything slower than this — i.e. any real "leave the page and come back" —
 *  revalidates. Kept well under a second of perceptible staleness. */
const DEDUP_WINDOW = 2_000;

/** Cap the cache so a long session toggling many filter combos can't grow it
 *  unbounded. Map preserves insertion order, so we evict the oldest entry. */
const MAX_ENTRIES = 24;

interface CacheEntry {
  data: JobsListResponse;
  fetchedAt: number;
}

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<JobsListResponse>>();

/** Deterministic cache key from the full filter set (same values → same key).
 *  Fixed field order keeps it stable regardless of object construction. */
function keyOf(f: JobListFilters): string {
  return JSON.stringify([
    f.page ?? 1,
    f.per_page ?? 0,
    f.sort_field ?? "",
    f.sort_direction ?? "",
    f.q ?? "",
    f.stages ?? "",
    f.only_open ?? false,
    f.exclude_archived ?? false,
    f.deadline_window ?? "all",
    f.has_overdue ?? false,
    f.has_open_tasks ?? false,
    f.has_unpaid ?? false,
    f.not_fully_invoiced ?? false,
  ]);
}

/** Fetch with in-flight de-dup: a second caller for an unsettled key gets the
 *  same Promise rather than a second request. The settled result is cached; the
 *  in-flight entry is cleared on settle so the next call can revalidate. */
function fetchDeduped(filters: JobListFilters, key: string): Promise<JobsListResponse> {
  const existing = inflight.get(key);
  if (existing) return existing;

  const p = jobsApi
    .listJobs(filters)
    .then((res) => {
      cache.set(key, { data: res, fetchedAt: Date.now() });
      if (cache.size > MAX_ENTRIES) {
        const oldest = cache.keys().next().value;
        if (oldest !== undefined) cache.delete(oldest);
      }
      return res;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, p);
  return p;
}

export interface UseJobsListResult {
  items: JobsListResponse["items"];
  total: number;
  totalPages: number;
  /** Cold load — no cached data to show yet. Drives the full-page spinner. */
  loading: boolean;
  /** Background revalidation in flight while cached rows are on screen. Drives a
   *  subtle "Refreshing…" indicator so a return-to-list visibly updates. */
  refreshing: boolean;
  error?: string;
}

interface State {
  data?: JobsListResponse;
  loading: boolean;
  refreshing: boolean;
  error?: string;
}

/** Cached-and-recent enough to skip a revalidation (StrictMode burst guard). */
function isWithinDedupWindow(key: string): boolean {
  const cached = cache.get(key);
  return !!cached && Date.now() - cached.fetchedAt < DEDUP_WINDOW;
}

export function useJobsList(filters: JobListFilters): UseJobsListResult {
  const key = keyOf(filters);

  // Seed from cache on first render so the very first paint after a back-nav is
  // instant (no loading flash) when we already hold this page.
  const [state, setState] = useState<State>(() => {
    const cached = cache.get(key);
    return cached
      ? { data: cached.data, loading: false, refreshing: !isWithinDedupWindow(key) }
      : { loading: true, refreshing: false };
  });

  useEffect(() => {
    let cancelled = false;
    const cached = cache.get(key);

    if (cached) {
      // Instant paint from cache (covers a filter change to an already-seen key,
      // not just remounts — the useState seed only fires once).
      if (isWithinDedupWindow(key)) {
        // Just fetched (synchronous mount burst) — show it, don't refetch.
        setState({ data: cached.data, loading: false, refreshing: false });
        return;
      }
      // Returning to the list → paint cached now, revalidate in the background.
      setState({ data: cached.data, loading: false, refreshing: true });
    } else {
      setState({ loading: true, refreshing: false });
    }

    fetchDeduped(filters, key)
      .then((res) => {
        if (!cancelled) setState({ data: res, loading: false, refreshing: false });
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof ApiError ? err.message : "Could not load jobs.";
        // Keep any cached rows on screen if a background revalidate fails.
        setState((s) => ({ data: s.data, loading: false, refreshing: false, error: message }));
      });

    // NOTE: cleanup deliberately does NOT clear the inflight entry — that's what
    // lets StrictMode's second effect run reuse the first run's request.
    return () => {
      cancelled = true;
    };
    // `key` encodes every filter; `filters` itself is rebuilt each render, so we
    // intentionally key the effect on the string, not the object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return {
    items: state.data?.items ?? [],
    total: state.data?.paging?.total ?? 0,
    totalPages: Math.max(1, state.data?.paging?.total_pages ?? 1),
    loading: state.loading,
    refreshing: state.refreshing,
    error: state.error,
  };
}
